import { useState, useEffect } from 'react';
import { Box, Typography, Chip, IconButton, Tooltip, CircularProgress } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link } from 'react-router-dom';
import { generateXPost, fetchXPostData } from '../lib/api';

const X_POST_EMAIL = 'ngswhidenori@gmail.com';

const XIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.259 5.631 5.905-5.631zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const snippet = (text, max = 180) => {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '…' : text;
};

const NewsCard = ({ article, index, selectedSite, onFilterBySite, onSaveArticle, isSaved, currentUserEmail }) => {
  const canPostToX = currentUserEmail === X_POST_EMAIL;
  const [generating, setGenerating] = useState(false);
  const [xPostData, setXPostData] = useState(null);

  // 特定ユーザーのみ: カード表示時にX_POST_LOGをプリフェッチ
  useEffect(() => {
    if (!canPostToX) return;
    fetchXPostData(article.id).then(data => { if (data) setXPostData(data); });
  }, [article.id, canPostToX]);

  // AIボタン: 確認ポップアップ → LLM実行 → X_POST_LOG に保存
  const handleGenerate = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = window.confirm(`以下の記事の投稿文をAIで生成しますか？\n\n${article.TITLE}`);
    if (!ok) return;
    setGenerating(true);
    try {
      const data = await generateXPost(article.id, currentUserEmail);
      if (data) {
        setXPostData(data);
      } else {
        alert('AI生成に失敗しました。時間をおいて再試行してください。');
      }
    } catch (err) {
      alert(`エラー: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  // Xボタン: プリフェッチ済みデータで同期的にwindow.open（iOS対応）
  const handleXPost = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!xPostData) {
      alert('投稿データが見つかりません。先にAI生成ボタンを押してください。');
      return;
    }
    const hashtagLine = xPostData.hashtags ? '\n' + xPostData.hashtags : '';
    const fullText = `${xPostData.post_text}\n${xPostData.article_url}${hashtagLine}`;
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(fullText)}`;
    window.open(tweetUrl, '_blank', 'noopener,noreferrer,width=550,height=450');
  };
  const siteName = article.SITE_NAME || 'NEWS';
  const isSelected = siteName === selectedSite;
  const description = snippet(article.HEADLINE1_BODY || article.HEADLINE1 || '');

  const handleSiteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onFilterBySite(siteName);
  };

  const handleSaveClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSaveArticle) onSaveArticle(article);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        py: 2.5,
        borderBottom: '1px solid #ccc8c2',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {/* Number */}
      {index !== undefined && (
        <Typography
          sx={{
            minWidth: 20,
            textAlign: 'right',
            color: '#b8b4ae',
            fontWeight: 700,
            fontSize: '1rem',
            lineHeight: 1.4,
            flexShrink: 0,
            fontFamily: 'inherit',
            mt: 0.1,
          }}
        >
          {index}
        </Typography>
      )}

      {/* Main content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Title */}
        <Typography
          component={Link}
          to={`/article/${article.id}`}
          sx={{
            display: 'block',
            fontWeight: 700,
            fontSize: '0.97rem',
            lineHeight: 1.45,
            color: '#1c1916',
            textDecoration: 'none',
            fontFamily: 'inherit',
            mb: 0.6,
            '&:hover': { color: '#ea580c' },
          }}
        >
          {article.TITLE}
        </Typography>

        {/* Description */}
        {description && (
          <Typography
            sx={{
              fontSize: '0.82rem',
              lineHeight: 1.6,
              color: '#78716c',
              fontFamily: 'inherit',
              mb: 1,
            }}
          >
            {description}
          </Typography>
        )}

        {/* Meta row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Chip
            label={siteName}
            size="small"
            onClick={handleSiteClick}
            sx={{
              height: 17,
              fontSize: '0.65rem',
              fontFamily: 'inherit',
              fontWeight: 700,
              letterSpacing: '0.04em',
              backgroundColor: isSelected ? '#ea580c' : '#ddd9d2',
              color: isSelected ? '#fff' : '#78716c',
              cursor: 'pointer',
              border: '1px solid',
              borderColor: isSelected ? '#ea580c' : '#ccc8c2',
              '&:hover': { backgroundColor: isSelected ? '#c2410c' : '#ccc8c2' },
            }}
          />

          <Box sx={{ flex: 1 }} />

          {canPostToX && (
            <>
              {/* AIボタン: LLM実行 → X_POST_LOG保存 */}
              <Tooltip title={generating ? '生成中...' : xPostData ? '再生成' : 'AI生成'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleGenerate}
                    disabled={generating}
                    sx={{
                      p: 0.3,
                      color: xPostData ? '#ea580c' : '#b8b4ae',
                      '&:hover': { color: '#ea580c', backgroundColor: 'transparent' },
                    }}
                  >
                    {generating
                      ? <CircularProgress size={11} sx={{ color: '#ccc8c2' }} />
                      : <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L9.1 9.1 2 12l7.1 2.9L12 22l2.9-7.1L22 12l-7.1-2.9z"/></svg>
                    }
                  </IconButton>
                </span>
              </Tooltip>

              {/* Xボタン: X_POST_LOGからデータ取得して投稿画面へ */}
              <Tooltip title="Xに投稿">
                <IconButton
                  size="small"
                  onClick={handleXPost}
                  sx={{
                    p: 0.3,
                    color: '#b8b4ae',
                    '&:hover': { color: '#1c1916', backgroundColor: 'transparent' },
                  }}
                >
                  <XIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          <Tooltip title={isSaved ? '保存解除' : '保存'}>
            <IconButton
              size="small"
              onClick={handleSaveClick}
              sx={{
                p: 0.3,
                color: isSaved ? '#ea580c' : '#b8b4ae',
                '&:hover': { color: '#ea580c', backgroundColor: 'transparent' },
              }}
            >
              {isSaved
                ? <FavoriteIcon sx={{ fontSize: 13 }} />
                : <FavoriteBorderIcon sx={{ fontSize: 13 }} />
              }
            </IconButton>
          </Tooltip>

          {article.URL && (
            <Tooltip title="元記事">
              <IconButton
                size="small"
                component="a"
                href={article.URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  p: 0.3,
                  color: '#b8b4ae',
                  '&:hover': { color: '#78716c', backgroundColor: 'transparent' },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Thumbnail (desktop only) */}
      {article.image && (
        <Box
          component={Link}
          to={`/article/${article.id}`}
          sx={{ flexShrink: 0, display: { xs: 'none', sm: 'block' } }}
        >
          <Box
            component="img"
            src={article.image}
            alt={article.TITLE}
            sx={{
              width: 88,
              height: 60,
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid #ccc8c2',
              opacity: 0.9,
              '&:hover': { opacity: 1 },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default NewsCard;
